import React, { useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import comms from '../comms';

const MAP_OPTIONS = { disableDefaultUI: true, clickableIcons: false, gestureHandling: 'greedy' };

function LocationCreate({ open, onClose, driverId, online, onToast, onCreated }) {
  const acRef   = useRef(null);
  const [name,    setName]    = useState('');
  const [pin,     setPin]     = useState(null);
  const [address, setAddress] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const mapsReady  = !!window.google?.maps;
  const placesReady = !!window.google?.maps?.places;

  const reset = () => { setName(''); setPin(null); setAddress(''); setError(''); };

  const handleClose = () => { reset(); onClose(); };

  const onPlaceChanged = () => {
    const place = acRef.current?.getPlace?.();
    const loc = place?.geometry?.location;
    if (!loc) return;
    setPin({ lat: loc.lat(), lng: loc.lng() });
    setAddress(place.formatted_address || place.name || '');
    if (!name && place.name) setName(place.name);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setAddress('My current location');
      },
      () => onToast('Could not read your location.'),
      { maximumAge: 10000, timeout: 8000 }
    );
  };

  const handleSave = async () => {
    setError('');
    if (!name.trim()) { setError('Give the location a name.'); return; }
    if (!pin)         { setError('Search an address or use current location first.'); return; }
    if (!online)      { setError('Connect to save a location.'); return; }
    setSaving(true);
    try {
      const res = await comms.createVehicleLocation({
        driverId,
        name: name.trim(),
        description: address || undefined,
        latitude:  pin.lat,
        longitude: pin.lng,
      });
      const ok = res?.status === true || res?.Status === true || res?.success === true;
      if (ok && (res?.location || res?.Location)) {
        const loc = res.location || res.Location;
        reset();
        onCreated(loc);
      } else {
        setError(res?.error || res?.message || "Couldn't save — try again.");
      }
    } catch (e) {
      setError(e?.message || "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 14px',
    border: '1.5px solid #E2E8F0', borderRadius: 12,
    fontSize: 14, color: '#0F172A', outline: 'none',
    background: '#F8FAFC', marginBottom: 10,
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { borderRadius: 20, margin: 16, maxHeight: 'calc(100% - 32px)', overflow: 'visible' } }}
    >
      <DialogTitle style={{ padding: '20px 20px 4px', fontSize: 17, fontWeight: 800, color: '#0F172A' }}>
        New location
      </DialogTitle>

      <DialogContent style={{ padding: '12px 20px 4px', overflow: 'visible' }}>
        {/* Inline error */}
        {error && (
          <div style={{
            background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
            padding: '10px 14px', marginBottom: 12,
            fontSize: 13, color: '#991B1B', fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        {/* Name */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
          Name
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. North Garage"
          style={inputStyle}
          onFocus={(e) => { e.target.style.borderColor = '#3B5BDB'; }}
          onBlur={(e)  => { e.target.style.borderColor = '#E2E8F0'; }}
        />

        {/* Address search */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
          Address
        </div>
        {placesReady ? (
          <Autocomplete
            onLoad={(ac) => { acRef.current = ac; }}
            onPlaceChanged={onPlaceChanged}
            options={{ fields: ['geometry', 'formatted_address', 'name'] }}
          >
            <input
              type="text"
              placeholder="Search address…"
              style={inputStyle}
              onFocus={(e) => { e.target.style.borderColor = '#3B5BDB'; }}
              onBlur={(e)  => { e.target.style.borderColor = '#E2E8F0'; }}
            />
          </Autocomplete>
        ) : (
          <div style={{
            ...inputStyle, color: '#94A3B8', display: 'flex', alignItems: 'center',
          }}>
            Map not ready — address search unavailable
          </div>
        )}

        {/* Use my location */}
        <button
          type="button"
          onClick={useMyLocation}
          style={{
            width: '100%', padding: '10px', marginBottom: 10, borderRadius: 12,
            border: '1.5px solid #E2E8F0', background: '#F8FAFC',
            fontSize: 13, fontWeight: 600, color: '#3B5BDB', cursor: 'pointer',
          }}
        >
          Use my current location
        </button>

        {/* Map preview */}
        {mapsReady && pin && (
          <div style={{ borderRadius: 14, overflow: 'hidden', marginBottom: 8, height: 200 }}>
            <GoogleMap
              center={pin}
              zoom={16}
              options={MAP_OPTIONS}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            >
              <Marker
                position={pin}
                draggable
                onDragEnd={(e) => setPin({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
              />
            </GoogleMap>
          </div>
        )}

        {pin && (
          <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>
            {address && <span style={{ display: 'block' }}>{address}</span>}
            Drag the pin to fine-tune · ({pin.lat.toFixed(5)}, {pin.lng.toFixed(5)})
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '8px 20px 20px', gap: 10 }}>
        <button
          type="button"
          onClick={handleClose}
          disabled={saving}
          style={{
            flex: 1, padding: '13px', borderRadius: 14, border: 'none',
            background: '#F1F5F9', color: '#64748B', fontSize: 14, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !online}
          style={{
            flex: 1, padding: '13px', borderRadius: 14, border: 'none',
            background: saving || !online ? '#93C5FD' : '#3B5BDB',
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: saving || !online ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : !online ? 'Offline' : 'Save location'}
        </button>
      </DialogActions>
    </Dialog>
  );
}

export default LocationCreate;
